import React from 'react';
import _round from 'lodash/round';
import cx from 'classnames';

import { barHeight, columnWidth, rowHeight } from '../constants';

interface DetectionBarProps {
    detections: SparseDateRange;
    isSelected: boolean;
    performancePeriod: SparseDateRange;
}

/**
 * Renders a colored bar with a ReportPeriodColumn that shows whether the period has detection
 * data. If a bar does not have data and is selected, a dotted border shows that there is the
 * missing data.
 *
 * Unlike a PerformanceBar, a DetectionBar can have partial data, so we have to do some math to draw
 * the bar itself.
 * @param props - {@link DetectionBarProps}
 * @returns JSX
 */
export const DetectionBar = ({
    detections,
    isSelected,
    performancePeriod,
}: DetectionBarProps): JSX.Element => {
    let dateDifference = 0;

    if (performancePeriod.start !== null && performancePeriod.end !== null) {
        dateDifference =
            performancePeriod.end.getTime() - performancePeriod.start.getTime();
    }

    let startGap = 0;

    if (detections.start !== null && performancePeriod.start !== null) {
        startGap = Math.max(
            0,
            detections.start.getTime() - performancePeriod.start.getTime(),
        );
    }

    const startGapPixels = _round((startGap / dateDifference) * columnWidth, 0);

    let endGap = 0;

    if (detections.end !== null && performancePeriod.end !== null) {
        endGap = Math.max(
            0,
            performancePeriod.end.getTime() - detections.end.getTime(),
        );
    }

    const endGapPixels = _round((endGap / dateDifference) * columnWidth, 0);

    let barWidth = 0;

    if (
        detections.start !== null &&
        detections.end !== null &&
        performancePeriod.start !== null &&
        performancePeriod.end !== null &&
        detections.end > performancePeriod.start &&
        detections.start < performancePeriod.end
    ) {
        barWidth = columnWidth - startGapPixels - endGapPixels - 2; // subtract 2 to account for border width in ReportPeriodColumn
    }

    return (
        <div
            className="flex flex-col justify-center"
            style={{ height: rowHeight }}
        >
            {barWidth > 0 && (
                <div
                    className="bg-quarternary-500"
                    style={{
                        marginLeft: startGapPixels,
                        height: barHeight,
                        width: barWidth,
                    }}
                />
            )}
            {barWidth === 0 && (
                <div
                    className={cx({
                        'border-2 border-dotted border-quarternary-500':
                            isSelected,
                    })}
                    style={{ height: barHeight }}
                />
            )}
        </div>
    );
};
